/**
 * Connectors — outbound HTTPS webhooks that receive a request's extracted data
 * when it is approved (and, optionally, when its fields are edited).
 *
 * Three views share one route: the list, a connector's detail (stats and
 * delivery log), and the create/edit form. Switching between them is local
 * state, not navigation, so a sidebar click on "Connectors" resets to the list.
 *
 * Search and the status/outcome filters are client-side: the list endpoint
 * returns every connector for the org, and the delivery log is the latest 50.
 */
import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { Link, useSearchParams, useLocation } from 'react-router-dom'
import { useToast } from '../context/ToastContext'
import Toggle from '../components/Toggle'
import api from '../services/api'
import { formatDateTime, formatDate, formatTimeAgo } from '../utils/dateUtils'
import { useAuth } from '../context/AuthContext'
import { usePageBreadcrumbs } from '../context/BreadcrumbContext'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import {
  Webhook, Plus, Trash2, Edit3, Save, Loader2, Send, CheckCircle2, XCircle, Clock,
  ChevronDown, ChevronRight, Eye, EyeOff, Copy, Check, RefreshCw, Zap, ArrowLeft,
  Globe, Shield, Hash, FileDown, Search, SearchX, X, KeyRound, ListPlus, Replace,
  Info, AlertTriangle, ShieldCheck, MoreHorizontal,
} from 'lucide-react'


const AVAILABLE_EVENTS = [
  { value: 'request.approved', label: 'Request Approved', description: 'Fires when a request is approved/published (auto when the template\'s auto-approve toggle is on, otherwise on manual Publish)' },
  { value: 'request.updated', label: 'Request Updated', description: 'Fires when a request’s fields are saved/edited (toggle controls upsert/append)' },
]

const UPDATE_MODES = [
  { value: 'append', label: 'Append', icon: ListPlus, description: 'Every delivery inserts a new row at the receiver.' },
  { value: 'upsert', label: 'Upsert by Request ID', icon: Replace, description: 'Updates the existing row keyed by request ID.' },
]

const CARD = 'rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-gray-800 dark:bg-gray-900'
const EASE = 'ease-[cubic-bezier(0.22,1,0.36,1)]'
const ICON_BUTTON = 'inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 px-3 text-[13px] font-medium text-gray-700 transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 enabled:active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800 dark:hover:text-gray-100'

const parseEvents = (events) => (events || '').split(',').map(e => e.trim()).filter(Boolean)

/** "https://host/path?q" → host and the rest, so the host can carry the weight. */
function splitUrl(url) {
  try {
    const u = new URL(url)
    const rest = `${u.pathname === '/' ? '' : u.pathname}${u.search}${u.hash}`
    return { host: u.host, rest }
  } catch {
    return { host: url || '', rest: '' }
  }
}

/** Pretty-prints JSON bodies; anything else is shown as it arrived. */
function formatBody(body) {
  if (body == null) return ''
  if (typeof body !== 'string') return JSON.stringify(body, null, 2)
  try { return JSON.stringify(JSON.parse(body), null, 2) } catch { return body }
}

function useCopy() {
  const [copied, setCopied] = useState(false)
  const timer = useRef(null)
  useEffect(() => () => clearTimeout(timer.current), [])
  const copy = useCallback((text) => {
    if (!text) return
    navigator.clipboard?.writeText(text)
    setCopied(true)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => setCopied(false), 1500)
  }, [])
  return [copied, copy]
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function CopyButton({ value, label = 'Copy', className = '' }) {
  const [copied, copy] = useCopy()
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); copy(value) }}
      aria-label={copied ? 'Copied' : label}
      title={copied ? 'Copied' : label}
      className={`inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 dark:hover:bg-gray-800 dark:hover:text-gray-200 ${className}`}
    >
      {copied
        ? <Check key="done" className="h-3.5 w-3.5 text-emerald-600 animate-copied dark:text-emerald-400" />
        : <Copy className="h-3.5 w-3.5" />}
    </button>
  )
}

/** The connector's tile; its tint follows the enabled state. */
function ConnectorMark({ active, size = 'md' }) {
  const box = size === 'lg' ? 'h-12 w-12 rounded-xl' : 'h-9 w-9 rounded-lg'
  const icon = size === 'lg' ? 'h-5 w-5' : 'h-4 w-4'
  return (
    <span className={`flex flex-shrink-0 items-center justify-center ring-1 ring-inset transition-colors duration-300 ${box} ${
      active
        ? 'bg-emerald-50 text-emerald-600 ring-emerald-600/15 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20'
        : 'bg-gray-50 text-gray-400 ring-gray-200 dark:bg-gray-800 dark:text-gray-500 dark:ring-gray-700'
    }`}>
      <Webhook className={icon} />
    </span>
  )
}

function StatusLabel({ active, live = false }) {
  if (!active) {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-gray-500 dark:text-gray-400">
        <span className="h-1.5 w-1.5 rounded-full border border-gray-400 dark:border-gray-500" />
        Disabled
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-emerald-700 dark:text-emerald-400">
      <span className="relative flex h-1.5 w-1.5">
        {live && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 motion-safe:animate-ping" />}
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
      </span>
      Active
    </span>
  )
}

function EventChip({ event }) {
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
      <Zap className="h-2.5 w-2.5 text-gray-400" />
      {event}
    </span>
  )
}

/**
 * Segmented control with a sliding thumb. The thumb is measured from the
 * selected button, so labels can be any width.
 */
function Segmented({ options, value, onChange, label }) {
  const refs = useRef({})
  const [thumb, setThumb] = useState(null)
  const [animate, setAnimate] = useState(false)
  const signature = options.map(o => `${o.value}:${o.count ?? ''}`).join('|')

  useLayoutEffect(() => {
    const el = refs.current[value]
    if (!el) return
    setThumb(prev => (prev && prev.left === el.offsetLeft && prev.width === el.offsetWidth)
      ? prev
      : { left: el.offsetLeft, width: el.offsetWidth })
  }, [value, signature])

  // The thumb lands in place on first paint and only slides afterwards.
  useEffect(() => {
    if (!thumb || animate) return undefined
    const id = requestAnimationFrame(() => setAnimate(true))
    return () => cancelAnimationFrame(id)
  }, [thumb, animate])

  return (
    <div role="radiogroup" aria-label={label} className="relative inline-flex h-9 flex-shrink-0 items-center rounded-lg bg-gray-100 p-0.5 dark:bg-gray-800">
      {thumb && (
        <span
          aria-hidden="true"
          className={`absolute inset-y-0.5 rounded-md bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-700 dark:ring-white/5 ${animate ? `transition-[left,width] duration-300 ${EASE}` : ''}`}
          style={{ left: thumb.left, width: thumb.width }}
        />
      )}
      {options.map(option => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            ref={el => { refs.current[option.value] = el }}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(option.value)}
            className={`relative z-[1] inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-[13px] font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 ${
              selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {option.label}
            {option.count != null && (
              <span className={`tabular-nums text-xs ${selected ? 'text-gray-500 dark:text-gray-400' : 'text-gray-400 dark:text-gray-500'}`}>
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

function BackLink({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group mb-3 inline-flex items-center gap-1.5 rounded-md text-xs font-medium lg:hidden text-gray-500 transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500 dark:text-gray-400 dark:hover:text-gray-100"
    >
      <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-200 group-hover:-translate-x-0.5" />
      {children}
    </button>
  )
}

// ── Status badge ─────────────────────────────────────────────────────────────

const DELIVERY_META = {
  success: { label: 'Success', icon: CheckCircle2, className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/15 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-400/20' },
  failed: { label: 'Failed', icon: XCircle, className: 'bg-red-50 text-red-700 ring-red-600/15 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-400/20' },
  pending: { label: 'Pending', icon: Clock, className: 'bg-amber-50 text-amber-700 ring-amber-600/15 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-400/20' },
}

function DeliveryBadge({ status }) {
  const meta = DELIVERY_META[status] || DELIVERY_META.pending
  const Icon = meta.icon
  return (
    <span key={status} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset animate-panel-in ${meta.className}`}>
      {status === 'pending' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
      {meta.label}
    </span>
  )
}

// ── Webhook form (create/edit) ───────────────────────────────────────────────

function FormSection({ title, description, children, first = false }) {
  return (
    <section className={`grid gap-x-10 gap-y-4 px-5 py-6 sm:px-6 lg:grid-cols-[15rem_minmax(0,1fr)] ${first ? '' : 'border-t border-gray-100 dark:border-gray-800'}`}>
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <div className="min-w-0 space-y-4">{children}</div>
    </section>
  )
}

function SecretInput({ id, value, onChange, visible, onToggleVisible, placeholder, autoComplete, noun = 'value' }) {
  return (
    <div className="relative">
      <input
        id={id}
        type={visible ? 'text' : 'password'}
        className="form-input !pr-10 font-mono text-sm"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
      />
      <button
        type="button"
        onClick={onToggleVisible}
        aria-label={visible ? `Hide ${noun}` : `Show ${noun}`}
        aria-pressed={visible}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
      >
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  )
}

function WebhookForm({ webhook, onSave, onCancel, saving, readOnly = false }) {
  // Get templateId from query param if present (for context)
  const [searchParams] = useSearchParams();
  const templateId = searchParams.get('templateId');
  const [mandateReview, setMandateReview] = useState(null);
  const [templateName, setTemplateName] = useState('');
  useEffect(() => {
    if (!templateId) return;
    // Fetch template details for mandate_review status
    // Use the shared api instance so the request carries the bearer token.
    api.get(`/templates/${templateId}`)
      .then(({ data }) => {
        setMandateReview(!!data.mandate_review);
        setTemplateName(data.name || '');
      })
      .catch(() => setMandateReview(null));
  }, [templateId]);
  const [name, setName] = useState(webhook?.name || '')
  const [url, setUrl] = useState(webhook?.url || '')
  const [events, setEvents] = useState(webhook?.events || 'request.approved')
  const [secret, setSecret] = useState(webhook?.secret || '')
  const [showSecret, setShowSecret] = useState(false)
  const [updateMode, setUpdateMode] = useState(webhook?.update_mode || 'append')
  const [requireMapping, setRequireMapping] = useState(Boolean(webhook?.require_payload_mapping))
  const [basicAuthUsername, setBasicAuthUsername] = useState(webhook?.basic_auth_username || '')
  const [basicAuthPassword, setBasicAuthPassword] = useState('')
  const [showBasicAuthPassword, setShowBasicAuthPassword] = useState(false)

  const selectedEvents = events.split(',').map(e => e.trim()).filter(Boolean)
  const urlInvalid = Boolean(url) && !url.startsWith('https://')

  const toggleEvent = (val) => {
    const set = new Set(selectedEvents)
    if (set.has(val)) set.delete(val)
    else set.add(val)
    if (set.size === 0) set.add('request.approved')
    setEvents([...set].join(','))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (readOnly) return
    onSave({
      name: name.trim(),
      url: url.trim(),
      events,
      secret: secret || undefined,
      update_mode: updateMode,
      require_payload_mapping: requireMapping,
      basic_auth_username: basicAuthUsername.trim() || undefined,
      basic_auth_password: basicAuthPassword || undefined,
    })
  }

  return (
    <form onSubmit={handleSubmit} className={CARD}>
      {/* Auto-approve (mandate_review) status for this template */}
      {templateId && (
        <div className="flex gap-3 rounded-t-2xl border-b border-gray-100 bg-navy-50/50 px-5 py-4 dark:border-gray-800 dark:bg-navy-500/5 sm:px-6">
          <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-navy-600 dark:text-navy-300" />
          <div className="text-sm">
            <p className="font-medium text-gray-900 dark:text-gray-100">Auto-Approve Requests</p>
            <p className="mt-0.5 text-gray-600 dark:text-gray-300">
              {mandateReview === null
                ? 'Loading...'
                : mandateReview
                  ? 'Manual review required before webhooks fire for this template.'
                  : 'Requests are auto-approved and webhooks fire immediately after extraction.'}
            </p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              This setting is controlled at the <b>template</b> level. It determines when webhooks are triggered.{' '}
              {templateName && (
                <>
                  Edit in <Link to={`/templates/${templateId}?tab=settings`} className="font-medium text-navy-700 underline underline-offset-2 dark:text-navy-300">{templateName} Settings</Link>.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <FormSection first title="Endpoint" description="Where Xtract sends deliveries. Only HTTPS endpoints are accepted.">
        <div>
          <label htmlFor="connector-name" className="form-label">Name</label>
          <input id="connector-name" type="text" className="form-input" value={name}
            onChange={(e) => setName(e.target.value)} placeholder="e.g. Purchase Order Export"
            required maxLength={100} />
        </div>
        <div>
          <label htmlFor="connector-url" className="form-label">Endpoint URL</label>
          <div className="relative">
            <Globe className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input id="connector-url" type="url"
              className={`form-input !pl-9 font-mono text-sm ${urlInvalid ? 'form-input-error' : ''}`}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://your-function.azurewebsites.net/api/webhook"
              aria-invalid={urlInvalid}
              aria-describedby={urlInvalid ? 'connector-url-error' : undefined}
              required />
          </div>
          {urlInvalid && (
            <p id="connector-url-error" className="mt-1.5 flex items-center gap-1 text-xs text-red-600 animate-panel-in dark:text-red-400">
              <AlertTriangle className="h-3 w-3" />
              URL must use HTTPS
            </p>
          )}
        </div>
      </FormSection>

      <FormSection title="Events" description="Which changes to a request trigger a delivery. At least one is required.">
        <div className="grid gap-3 sm:grid-cols-2">
          {AVAILABLE_EVENTS.map(evt => {
            const active = selectedEvents.includes(evt.value)
            return (
              <button key={evt.value} type="button" role="checkbox" aria-checked={active}
                onClick={() => toggleEvent(evt.value)}
                className={`group flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 ${
                  active
                    ? 'border-navy-400 bg-navy-50/60 shadow-sm dark:border-navy-400/50 dark:bg-navy-500/10'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/60 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800/40'
                }`}>
                <span className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors duration-200 ${
                  active
                    ? 'border-navy-600 bg-navy-600 text-white dark:border-navy-400 dark:bg-navy-400 dark:text-gray-900'
                    : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
                }`}>
                  {active && <Check className="h-3 w-3 animate-copied" strokeWidth={3} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{evt.label}</span>
                  <span className="mt-0.5 block font-mono text-[11px] text-gray-500 dark:text-gray-400">{evt.value}</span>
                  <span className="mt-1.5 block text-xs leading-5 text-gray-500 dark:text-gray-400">{evt.description}</span>
                </span>
              </button>
            )
          })}
        </div>
      </FormSection>

      <FormSection title="Update mode" description="How the receiver should treat repeat deliveries for the same request.">
        <div role="radiogroup" aria-label="Update mode" className="grid gap-3 sm:grid-cols-2">
          {UPDATE_MODES.map(mode => {
            const active = updateMode === mode.value
            const Icon = mode.icon
            return (
              <button key={mode.value} type="button" role="radio" aria-checked={active}
                onClick={() => setUpdateMode(mode.value)}
                className={`flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 ${
                  active
                    ? 'border-navy-400 bg-navy-50/60 shadow-sm dark:border-navy-400/50 dark:bg-navy-500/10'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/60 dark:border-gray-700 dark:hover:border-gray-600 dark:hover:bg-gray-800/40'
                }`}>
                <span className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border transition-colors duration-200 ${
                  active ? 'border-navy-600 dark:border-navy-400' : 'border-gray-300 dark:border-gray-600'
                }`}>
                  <span className={`h-2 w-2 rounded-full bg-navy-600 transition-transform duration-200 dark:bg-navy-400 ${active ? 'scale-100' : 'scale-0'}`} />
                </span>
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-gray-100">
                    <Icon className="h-3.5 w-3.5 text-gray-400" />
                    {mode.label}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-gray-500 dark:text-gray-400">{mode.description}</span>
                </span>
              </button>
            )
          })}
        </div>
      </FormSection>

      <FormSection title="Payload mapping" description="Stop this connector sending anything but your agreed format.">
        <label className={`flex items-start justify-between gap-4 ${readOnly ? '' : 'cursor-pointer'}`}>
          <div className="min-w-0">
            <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Require a payload mapping</span>
            <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
              When on, a template linked to this connector must have a Map Payload. Without one, nothing is sent and the delivery is logged as failed with the reason.
              When off, a template with no Map Payload sends Xtract's standard format.
            </p>
          </div>
          <Toggle
            checked={requireMapping}
            onChange={() => setRequireMapping(v => !v)}
            disabled={readOnly}
            title={readOnly ? READ_ONLY_MESSAGE : (requireMapping ? 'Do not require a payload mapping' : 'Require a payload mapping')}
          />
        </label>
      </FormSection>

      <FormSection title="Security" description="Let your endpoint verify that a delivery came from Xtract.">
        <div>
          <label htmlFor="connector-secret" className="form-label">Signing secret</label>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <SecretInput
                id="connector-secret"
                value={secret}
                onChange={(e) => setSecret(e.target.value)}
                visible={showSecret}
                onToggleVisible={() => setShowSecret(!showSecret)}
                noun="secret"
                placeholder="Auto-generated if left empty"
              />
            </div>
            {secret && (
              <CopyButton value={secret} label="Copy secret" className="h-[42px] w-[42px] rounded-lg border border-gray-200 dark:border-gray-700" />
            )}
          </div>
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
            Used to sign payloads with HMAC-SHA256. Sent in the <code className="rounded bg-gray-100 px-1 font-mono text-[11px] dark:bg-gray-800">X-Xtract-Signature</code> header.
          </p>
        </div>

        <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-gray-400" />
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Basic authentication</p>
            <span className="text-xs text-gray-400">Optional</span>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Sends an <code className="rounded bg-gray-100 px-1 font-mono text-[11px] dark:bg-gray-800">Authorization: Basic</code> header on every delivery. Password is encrypted before storage.
          </p>
          <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <label htmlFor="connector-basic-user" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">Username</label>
              <input id="connector-basic-user" type="text" className="form-input text-sm" value={basicAuthUsername}
                onChange={e => setBasicAuthUsername(e.target.value)}
                placeholder="username" autoComplete="off" />
            </div>
            <div>
              <label htmlFor="connector-basic-password" className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">
                Password
                {webhook?.basic_auth_username && (
                  <span className="ml-1.5 font-normal text-gray-400">(leave blank to keep existing)</span>
                )}
              </label>
              <SecretInput
                id="connector-basic-password"
                value={basicAuthPassword}
                onChange={e => setBasicAuthPassword(e.target.value)}
                visible={showBasicAuthPassword}
                onToggleVisible={() => setShowBasicAuthPassword(!showBasicAuthPassword)}
                noun="password"
                placeholder={webhook?.basic_auth_username ? '••••••••' : 'password'}
                autoComplete="new-password"
              />
            </div>
          </div>
        </div>
      </FormSection>

      <div className="sticky bottom-0 z-10 flex items-center justify-end gap-3 rounded-b-2xl border-t border-gray-100 bg-white/90 px-5 py-3.5 backdrop-blur supports-[backdrop-filter]:bg-white/75 dark:border-gray-800 dark:bg-gray-900/90 dark:supports-[backdrop-filter]:bg-gray-900/75 sm:px-6">
        <button type="button" onClick={onCancel}
          className="btn btn-secondary text-sm">Cancel</button>
        <button type="submit" disabled={readOnly || saving || !name.trim() || !url.trim() || !url.startsWith('https://')}
          title={readOnly ? READ_ONLY_MESSAGE : undefined}
          className="btn btn-primary flex items-center gap-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {webhook ? 'Save Changes' : 'Create Connector'}
        </button>
      </div>
    </form>
  )
}

// ── Delete confirmation ──────────────────────────────────────────────────────

//   state.stage 'confirm' -> initial "are you sure?"
//   state.stage 'linked'  -> backend returned 409: the webhook is linked to
//                            state.linkedCount templates and needs force.
function DeleteDialog({ webhook, state, deleting, onCancel, onConfirm }) {
  const cancelRef = useRef(null)
  useEffect(() => { cancelRef.current?.focus() }, [state.stage])
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !deleting) onCancel() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [deleting, onCancel])

  const linked = state.stage === 'linked'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-gray-950/50 backdrop-blur-[2px] animate-backdrop-in"
        onClick={() => { if (!deleting) onCancel() }}
      />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-connector-title"
        aria-describedby="delete-connector-desc"
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl animate-fade-in dark:border-gray-700 dark:bg-gray-900"
      >
        <div key={state.stage} className="px-6 pb-5 pt-6 animate-panel-in">
          <div className={`mb-4 flex h-10 w-10 items-center justify-center rounded-full ${linked ? 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300' : 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300'}`}>
            {linked ? <AlertTriangle className="h-5 w-5" /> : <Trash2 className="h-5 w-5" />}
          </div>
          {linked ? (
            <>
              <h3 id="delete-connector-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Webhook is linked to {state.linkedCount ?? 'one or more'} template{state.linkedCount === 1 ? '' : 's'}
              </h3>
              <p id="delete-connector-desc" className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                Deleting this webhook will immediately stop delivering events for {state.linkedCount === 1 ? 'that template' : 'those templates'}.
                Published records will not be pushed to this endpoint anymore.
              </p>
            </>
          ) : (
            <>
              <h3 id="delete-connector-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Delete webhook?
              </h3>
              <p id="delete-connector-desc" className="mt-1.5 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                This will permanently remove the webhook and its delivery history. This cannot be undone.
              </p>
            </>
          )}
          <div className="mt-4 flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800/60">
            <ConnectorMark active={webhook.is_active} />
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{webhook.name}</p>
              <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">{webhook.url}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2.5 border-t border-gray-100 bg-gray-50/80 px-6 py-4 dark:border-gray-800 dark:bg-gray-800/40">
          <button
            ref={cancelRef}
            onClick={onCancel}
            disabled={deleting}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Keep it
          </button>
          <button
            onClick={() => onConfirm(linked)}
            disabled={deleting}
            className="btn btn-danger gap-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
            {linked ? 'Delete anyway' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Delivery log ─────────────────────────────────────────────────────────────

// One column per fact, as in the original log. Every column grows in
// proportion from its minimum, so spare width spreads evenly instead of
// pooling beside one column. Below md the row keeps status and event and the
// rest moves to the subline and expanded details; from md the minimums fit a
// laptop with the sidebar open, and narrower widths scroll inside the card.
const DELIVERY_GRID = 'grid-cols-[7.5rem_minmax(0,1fr)_1rem] gap-x-4 md:grid-cols-[minmax(7rem,1fr)_minmax(8.5rem,1.5fr)_minmax(3.25rem,0.5fr)_minmax(4rem,0.7fr)_minmax(8.5rem,1.3fr)_minmax(4.5rem,0.7fr)_minmax(4.5rem,0.6fr)_1rem] md:gap-x-3 xl:gap-x-4'

function CodeBlock({ label, value, tone = 'neutral' }) {
  const danger = tone === 'danger'
  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex items-center justify-between">
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${danger ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>{label}</p>
        <CopyButton value={value} label={`Copy ${label.toLowerCase()}`} />
      </div>
      <pre className={`max-h-64 overflow-auto rounded-lg border p-3 font-mono text-xs leading-relaxed ${
        danger
          ? 'whitespace-pre-wrap border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300'
          : 'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-800 dark:bg-gray-950 dark:text-gray-300'
      }`}>
        {value}
      </pre>
    </div>
  )
}

const ROW_ACTION = 'inline-flex h-7 items-center gap-1 rounded-md border px-2 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 disabled:cursor-not-allowed disabled:opacity-50'

/** Phone width: these columns live in the row's subline and expanded details. */
const WIDE = 'hidden md:flex md:items-center'

function DeliveryRow({ delivery, index, open, onToggleOpen, onRetry, retrying, readOnly }) {
  // Details mount on first open and stay mounted, so closing animates too.
  const [mounted, setMounted] = useState(open)
  if (open && !mounted) setMounted(true)

  // Extract a download URL from the response body — ONLY when the
  // receiving endpoint explicitly returned one of these known keys.
  // (Historically some customer webhooks doubled as a CSV delivery
  // mechanism before the dedicated Output Destinations feature
  // existed; this is a narrow backward-compat affordance for those,
  // not a general-purpose link scraper. Do NOT fall back to
  // regex-matching any URL-shaped substring in the raw response —
  // that produced a "CSV" button on every delivery regardless of
  // whether the webhook has anything to do with CSV export, and
  // often linked to unrelated/garbage URLs.)
  let downloadUrl = null
  if (delivery.response_body) {
    try {
      const parsed = typeof delivery.response_body === 'string'
        ? JSON.parse(delivery.response_body)
        : delivery.response_body
      downloadUrl = parsed?.download_url || parsed?.csv_url || parsed?.file_url || null
      if (downloadUrl && !downloadUrl.startsWith('http')) downloadUrl = null
    } catch {
      // Not JSON - no structured signal that this response contains
      // a CSV/file download link, so don't show the button.
    }
  }

  const timestamp = delivery.completed_at || delivery.created_at
  const ok = delivery.http_status >= 200 && delivery.http_status < 300
  const isTest = !(delivery.request_id > 0)
  const failed = delivery.status === 'failed'

  const requestCell = isTest ? (
    <span className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400">Test</span>
  ) : (
    <Link
      to={`/requests/${delivery.request_id}`}
      onClick={(e) => e.stopPropagation()}
      aria-label={`Request #${delivery.request_id}`}
      className="inline-flex items-center gap-0.5 rounded text-[13px] tabular-nums text-gray-700 transition-colors hover:text-navy-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 dark:text-gray-300 dark:hover:text-navy-300"
    >
      <Hash className="h-3 w-3 text-gray-400" />{delivery.request_id}
    </Link>
  )

  const csvLink = downloadUrl && (
    <a
      href={downloadUrl}
      target="_blank"
      rel="noopener noreferrer"
      title="Download the CSV this endpoint returned"
      className={`${ROW_ACTION} border-gray-200 text-gray-700 hover:border-navy-300 hover:bg-navy-50 hover:text-navy-700 dark:border-gray-700 dark:text-gray-200 dark:hover:border-navy-400/40 dark:hover:bg-navy-500/10 dark:hover:text-navy-200`}
      onClick={(e) => e.stopPropagation()}
    >
      <FileDown className="h-3 w-3" />
      CSV
    </a>
  )

  return (
    <li
      id={`delivery-${delivery.id}`}
      className="scroll-mt-24 animate-rise-in"
      style={{ '--rise-delay': `${Math.min(index, 12) * 24}ms` }}
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={onToggleOpen}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) return
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleOpen() }
        }}
        className={`grid ${DELIVERY_GRID} cursor-pointer items-center px-4 py-3 text-left transition-colors duration-150 focus:outline-none focus-visible:bg-navy-50/60 dark:focus-visible:bg-navy-500/10 sm:px-5 ${
          open ? 'bg-gray-50/80 dark:bg-gray-800/40' : 'hover:bg-gray-50/80 dark:hover:bg-gray-800/30'
        }`}
      >
        {/* Status */}
        <div className="flex items-center gap-1">
          <DeliveryBadge status={delivery.status} />
          {failed && (
            <button
              type="button"
              title={readOnly ? READ_ONLY_MESSAGE : 'Retry this delivery'}
              aria-label="Retry this delivery"
              className="rounded-md p-1 text-red-500 transition-colors hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:hover:text-red-300"
              onClick={(e) => { e.stopPropagation(); if (!readOnly) onRetry(delivery.id) }}
              disabled={readOnly || retrying}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${retrying ? 'animate-spin' : ''}`} />
            </button>
          )}
        </div>

        {/* Event */}
        <div className="min-w-0">
          <p className="truncate font-mono text-[13px] leading-5 text-gray-900 dark:text-gray-100">{delivery.event}</p>
          <p className="mt-0.5 flex min-w-0 items-center gap-1.5 whitespace-nowrap text-xs leading-4 text-gray-500 dark:text-gray-400 md:hidden">
            {isTest ? 'Test delivery' : `Request #${delivery.request_id}`}
            <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">&middot;</span>
            <span className="truncate">{formatTimeAgo(timestamp) || '-'}</span>
          </p>
        </div>

        {/* HTTP */}
        <div className={WIDE}>
          {delivery.http_status ? (
            <span className={`font-mono text-[13px] font-semibold leading-none tabular-nums ${ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'}`}>
              {delivery.http_status}
            </span>
          ) : (
            <span className="text-[13px] text-gray-400">-</span>
          )}
        </div>

        {/* Request */}
        <div className={WIDE}>{requestCell}</div>

        {/* Timestamp */}
        <div className={`${WIDE} min-w-0`}>
          <span className="block truncate text-[13px] tabular-nums text-gray-700 dark:text-gray-300" title={formatTimeAgo(timestamp)}>
            {formatDateTime(timestamp)}
          </span>
        </div>

        {/* Duration */}
        <div className={WIDE}>
          <span className="font-mono text-[13px] leading-none tabular-nums text-gray-500 dark:text-gray-400">
            {delivery.response_time_ms ? `${delivery.response_time_ms} ms` : '-'}
          </span>
        </div>

        {/* Download */}
        <div className={WIDE}>
          {csvLink || <span className="text-[13px] text-gray-300 dark:text-gray-600">-</span>}
        </div>

        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-300 ${EASE} ${open ? 'rotate-180' : ''}`} />
      </div>

      <div className="reveal-rows" data-open={open}>
        <div>
          {mounted && (
            <div className="space-y-4 border-t border-gray-100 bg-gray-50/80 px-4 pb-5 pt-4 dark:border-gray-800 dark:bg-gray-800/30 sm:px-5">
              {/* Phone width hides these columns; repeat them here. */}
              <dl className="grid grid-cols-2 gap-3 text-xs md:hidden">
                <div><dt className="text-gray-400">HTTP</dt><dd className="font-mono text-gray-700 dark:text-gray-300">{delivery.http_status || '-'}</dd></div>
                <div><dt className="text-gray-400">Duration</dt><dd className="font-mono text-gray-700 dark:text-gray-300">{delivery.response_time_ms ? `${delivery.response_time_ms} ms` : '-'}</dd></div>
                <div><dt className="text-gray-400">Request</dt><dd className="mt-0.5">{requestCell}</dd></div>
                <div><dt className="text-gray-400">Timestamp</dt><dd className="text-gray-700 dark:text-gray-300">{formatDateTime(timestamp)}</dd></div>
                {csvLink && <div className="col-span-2"><dt className="mb-1 text-gray-400">Download</dt><dd>{csvLink}</dd></div>}
              </dl>
              {delivery.error_message && (
                <CodeBlock label="Error" value={delivery.error_message} tone="danger" />
              )}
              {(delivery.request_payload || delivery.response_body) && (
                <div className="grid gap-4 xl:grid-cols-2">
                  {delivery.request_payload && (
                    <CodeBlock label="Request Payload" value={formatBody(delivery.request_payload)} />
                  )}
                  {delivery.response_body && (
                    <CodeBlock label="Response Body" value={formatBody(delivery.response_body)} />
                  )}
                </div>
              )}
              {!delivery.error_message && !delivery.request_payload && !delivery.response_body && (
                <p className="text-xs italic text-gray-400">No additional details available.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

// ── Detail: health, configuration, actions ───────────────────────────────────

const HEALTH_SLOTS = 50
const TICK_TONE = {
  success: 'bg-emerald-500 dark:bg-emerald-400',
  failed: 'bg-red-500 dark:bg-red-400',
  pending: 'bg-amber-400 dark:bg-amber-300',
}

function Metric({ label, value, tone = 'neutral', loading }) {
  const tones = {
    neutral: 'text-gray-900 dark:text-gray-100',
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400',
  }
  return (
    <div className="min-w-0 sm:border-l sm:border-gray-100 sm:pl-5 sm:first:border-l-0 sm:first:pl-0 dark:border-gray-800">
      <dt className="text-xs text-gray-500 dark:text-gray-400">{label}</dt>
      {loading ? (
        <dd className="skeleton mt-1.5 h-6 w-14" />
      ) : (
        <dd key={value} className={`mt-0.5 text-xl font-semibold tabular-nums tracking-tight animate-panel-in ${tones[tone]}`}>{value}</dd>
      )}
    </div>
  )
}

/**
 * Health strip: one bar per delivery, oldest on the left, padded out to the
 * 50 the log holds. Hovering a bar describes it; clicking opens it in the log.
 */
function DeliveryHealth({ deliveries, loading, successCount, failedCount, successRate, avgResponse, onSelect }) {
  const [hovered, setHovered] = useState(null)
  const ordered = useMemo(() => [...deliveries].reverse(), [deliveries])
  const empty = Math.max(0, HEALTH_SLOTS - ordered.length)
  const last = deliveries[0]
  const rateTone = successRate == null ? 'neutral' : successRate >= 95 ? 'success' : successRate >= 80 ? 'warning' : 'danger'
  const describe = (d) => {
    const meta = DELIVERY_META[d.status] || DELIVERY_META.pending
    return [meta.label, d.event, d.http_status ? `HTTP ${d.http_status}` : null, formatDateTime(d.completed_at || d.created_at)].filter(Boolean).join(' · ')
  }

  return (
    <section className={`${CARD} flex h-full flex-col p-5`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delivery health</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {loading
            ? 'Loading…'
            : last
              ? <>Last delivery {formatTimeAgo(last.completed_at || last.created_at).toLowerCase()}</>
              : 'No deliveries yet'}
        </p>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-y-4 sm:grid-cols-4">
        <Metric label="Success rate" value={successRate == null ? '-' : `${successRate}%`} tone={rateTone} loading={loading} />
        <Metric label="Successful" value={successCount} loading={loading} />
        <Metric label="Failed" value={failedCount} tone={failedCount ? 'danger' : 'neutral'} loading={loading} />
        <Metric label="Avg response" value={avgResponse == null ? '-' : `${avgResponse} ms`} loading={loading} />
      </dl>

      <div className="flex flex-1 flex-col pt-5">
        {loading ? (
          <div className="skeleton min-h-[2rem] w-full flex-1" />
        ) : (
          <div
            className="flex min-h-[2rem] flex-1 items-stretch gap-[2px] sm:gap-[3px]"
            onMouseLeave={() => setHovered(null)}
          >
            {Array.from({ length: empty }, (_, i) => (
              <span key={`empty-${i}`} aria-hidden="true" className="flex-1 rounded-[2px] bg-gray-100 dark:bg-gray-800" />
            ))}
            {ordered.map((d, i) => (
              <button
                key={d.id}
                type="button"
                aria-label={describe(d)}
                onMouseEnter={() => setHovered(d)}
                onFocus={() => setHovered(d)}
                onBlur={() => setHovered(null)}
                onClick={() => onSelect(d.id)}
                style={{ '--tick-delay': `${Math.min(i, 40) * 18}ms` }}
                className={`flex-1 rounded-[2px] transition-opacity duration-200 animate-tick-in focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/50 focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-900 ${TICK_TONE[d.status] || TICK_TONE.pending} ${hovered && hovered.id !== d.id ? 'opacity-40' : 'opacity-100'}`}
              />
            ))}
          </div>
        )}
        <div className="mt-2 flex h-4 items-center justify-between gap-3 text-[11px] text-gray-400 dark:text-gray-500">
          {hovered ? (
            <span key={hovered.id} className="truncate text-gray-600 animate-panel-in dark:text-gray-300">{describe(hovered)}</span>
          ) : (
            <>
              <span>{deliveries.length ? `Latest ${deliveries.length} of up to ${HEALTH_SLOTS}` : 'Deliveries will appear here'}</span>
              <span className="hidden items-center gap-3 sm:flex">
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-[2px] bg-emerald-500 dark:bg-emerald-400" />Success</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-[2px] bg-red-500 dark:bg-red-400" />Failed</span>
                <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-[2px] bg-amber-400 dark:bg-amber-300" />Pending</span>
              </span>
            </>
          )}
        </div>
      </div>
    </section>
  )
}

function ConfigRow({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <dt className="flex-shrink-0 text-xs leading-5 text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="flex min-w-0 flex-wrap justify-end gap-1 text-right text-[13px] leading-5 text-gray-900 dark:text-gray-100">{children}</dd>
    </div>
  )
}

function ConfigurationCard({ webhook, onEdit, readOnly }) {
  const events = parseEvents(webhook.events)
  const mode = UPDATE_MODES.find(m => m.value === webhook.update_mode) || UPDATE_MODES[0]
  const ModeIcon = mode.icon
  return (
    <section className={`${CARD} h-full px-5 py-4`}>
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Configuration</h2>
        <button
          type="button"
          onClick={() => !readOnly && onEdit()}
          disabled={readOnly}
          title={readOnly ? READ_ONLY_MESSAGE : undefined}
          className="rounded text-xs font-medium text-navy-700 transition-colors hover:text-navy-900 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 disabled:cursor-not-allowed disabled:opacity-50 dark:text-navy-300 dark:hover:text-navy-200"
        >
          Edit
        </button>
      </div>
      <dl className="mt-2 divide-y divide-gray-100 dark:divide-gray-800">
        <ConfigRow label="Events">
          {events.map(evt => <EventChip key={evt} event={evt} />)}
        </ConfigRow>
        <ConfigRow label="Update mode">
          <span className="inline-flex items-center gap-1.5"><ModeIcon className="h-3.5 w-3.5 text-gray-400" />{mode.label}</span>
        </ConfigRow>
        <ConfigRow label="Payload mapping">
          {webhook.require_payload_mapping
            ? <span>Required</span>
            : <span className="text-gray-500 dark:text-gray-400">Optional</span>}
        </ConfigRow>
        <ConfigRow label="Signing">
          {webhook.secret
            ? <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />HMAC-SHA256</span>
            : <span className="text-gray-500 dark:text-gray-400">Not signed</span>}
        </ConfigRow>
        <ConfigRow label="Basic auth">
          {webhook.basic_auth_username
            ? <span className="inline-flex min-w-0 items-center gap-1.5"><KeyRound className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" /><span className="truncate font-mono text-xs">{webhook.basic_auth_username}</span></span>
            : <span className="text-gray-500 dark:text-gray-400">Not configured</span>}
        </ConfigRow>
        <ConfigRow label="Created">
          <span className="tabular-nums">{webhook.created_at ? formatDate(webhook.created_at) : '-'}</span>
        </ConfigRow>
      </dl>
    </section>
  )
}

/** Overflow menu for the less frequent (and the destructive) actions. */
function MoreMenu({ items }) {
  const [open, setOpen] = useState(false)
  const rootRef = useRef(null)
  const buttonRef = useRef(null)
  const listRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onPointer = (e) => { if (!rootRef.current?.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onPointer)
    listRef.current?.querySelector('[role="menuitem"]:not([disabled])')?.focus()
    return () => document.removeEventListener('mousedown', onPointer)
  }, [open])

  const close = () => { setOpen(false); buttonRef.current?.focus() }
  const onKeyDown = (e) => {
    const options = Array.from(listRef.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || [])
    const idx = options.indexOf(document.activeElement)
    if (e.key === 'Escape') { e.preventDefault(); close() }
    else if (e.key === 'ArrowDown') { e.preventDefault(); options[(idx + 1) % options.length]?.focus() }
    else if (e.key === 'ArrowUp') { e.preventDefault(); options[(idx - 1 + options.length) % options.length]?.focus() }
    else if (e.key === 'Tab') setOpen(false)
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More actions"
        className={`${ICON_BUTTON} px-2.5 ${open ? 'bg-gray-50 dark:bg-gray-800' : ''}`}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div
          ref={listRef}
          role="menu"
          onKeyDown={onKeyDown}
          className="absolute right-0 z-30 mt-2 w-52 origin-top-right rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl ring-1 ring-black/5 animate-fade-in dark:border-gray-700 dark:bg-gray-800"
        >
          {items.map((item, i) => {
            const Icon = item.icon
            return (
              <React.Fragment key={item.label}>
                {item.danger && i > 0 && <div className="my-1 h-px bg-gray-100 dark:bg-gray-700" />}
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  title={item.title}
                  onClick={() => { setOpen(false); item.onClick() }}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${
                    item.danger
                      ? 'text-red-600 hover:bg-red-50 focus-visible:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:focus-visible:bg-red-500/10'
                      : 'text-gray-700 hover:bg-gray-50 focus-visible:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700/60 dark:focus-visible:bg-gray-700/60'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </button>
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── Webhook detail panel ─────────────────────────────────────────────────────

function WebhookDetailPanel({ webhook, onBack, onDelete, onToggle, onTest, toast, readOnly = false }) {
  const [deliveries, setDeliveries] = useState([])
  const [loadingDeliveries, setLoadingDeliveries] = useState(true)
  const [hasLoadedDeliveries, setHasLoadedDeliveries] = useState(false)
  const [testing, setTesting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [expandedDelivery, setExpandedDelivery] = useState(null)
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [retryingId, setRetryingId] = useState(null)
  const [outcome, setOutcome] = useState('all')

  const loadDeliveries = useCallback(async () => {
    setLoadingDeliveries(true)
    try {
      const resp = await api.get(`/webhooks/${webhook.id}/deliveries?limit=50`)
      setDeliveries(resp.data.deliveries || [])
    } catch { setDeliveries([]) }
    finally { setLoadingDeliveries(false); setHasLoadedDeliveries(true) }
  }, [webhook.id])

  useEffect(() => { loadDeliveries() }, [loadDeliveries])

  // Auto-refresh deliveries every 10s so status updates without page reload
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const resp = await api.get(`/webhooks/${webhook.id}/deliveries?limit=50`)
        const latest = resp.data.deliveries || []
        setDeliveries(latest)
        // Clear spinner if the retried delivery is no longer pending
        setRetryingId(prev => {
          if (!prev) return null
          const retried = latest.find(d => d.id === prev)
          if (retried && retried.status !== 'pending') return null
          return prev
        })
      } catch { /* ignore */ }
    }, 10000)
    return () => clearInterval(interval)
  }, [webhook.id])

  const handleRetryDelivery = async (deliveryId) => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setRetryingId(deliveryId)
    // Optimistic update: show "pending" immediately
    setDeliveries(prev => prev.map(d => d.id === deliveryId ? { ...d, status: 'pending' } : d))
    try {
      await api.post(`/webhooks/deliveries/${deliveryId}/retry`)
      toast.success('Retry fired', 'Waiting for result...')
      // Poll until this delivery's status changes from pending
      let attempts = 0
      const poll = setInterval(async () => {
        attempts++
        try {
          const resp = await api.get(`/webhooks/${webhook.id}/deliveries?limit=50`)
          const latest = resp.data.deliveries || []
          setDeliveries(latest)
          const retried = latest.find(d => d.id === deliveryId)
          if (!retried || retried.status !== 'pending' || attempts >= 60) {
            clearInterval(poll)
            setRetryingId(null)
          }
        } catch {
          clearInterval(poll)
          setRetryingId(null)
        }
      }, 5000)
    } catch (err) {
      toast.error('Retry failed', err?.response?.data?.error || 'Unknown error')
      // Revert optimistic update back to failed
      setDeliveries(prev => prev.map(d => d.id === deliveryId ? { ...d, status: 'failed' } : d))
      setRetryingId(null)
      // Refresh deliveries so UI reflects current state
      try {
        const resp = await api.get(`/webhooks/${webhook.id}/deliveries?limit=50`)
        setDeliveries(resp.data.deliveries || [])
      } catch { /* ignore */ }
    }
  }

  const handleTest = async () => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setTesting(true)
    try {
      await onTest(webhook.id)
      setTimeout(() => { loadDeliveries(); setTesting(false) }, 2000)
    } catch { setTesting(false) }
  }

  // Delete confirmation modal state.
  //   null                                  -> modal closed
  //   { stage: 'confirm' }                  -> initial "are you sure?" pop-up
  //   { stage: 'linked', linkedCount: N }   -> shown when backend returns 409
  //                                            because the webhook is linked
  //                                            to N templates; needs force.
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const handleDelete = () => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setDeleteConfirm({ stage: 'confirm' })
  }

  const performDelete = async (force = false) => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setDeleting(true)
    try {
      await onDelete(webhook.id, { force })
      setDeleteConfirm(null)
      onBack()
    } catch (err) {
      if (err?.response?.status === 409) {
        // Webhook is linked to templates; switch the modal to the warning
        // stage so the admin can confirm a forced delete.
        const linkedCount = err.response?.data?.linked_templates_count ?? null
        setDeleteConfirm({ stage: 'linked', linkedCount })
      } else {
        setDeleteConfirm(null)
      }
    } finally {
      setDeleting(false)
    }
  }

  const handleUpdate = async (data) => {
    if (readOnly) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setSaving(true)
    try {
      await api.put(`/webhooks/${webhook.id}`, data)
      toast.success('Webhook Updated', 'Your webhook has been updated.')
      setEditing(false)
      onBack()
    } catch (err) {
      toast.error('Update Failed', err?.response?.data?.error || 'Could not update webhook.')
    } finally { setSaving(false) }
  }

  const closeDeleteDialog = useCallback(() => setDeleteConfirm(null), [])

  const successCount = deliveries.filter(d => d.status === 'success').length
  const failedCount = deliveries.filter(d => d.status === 'failed').length
  const settled = successCount + failedCount
  const successRate = settled ? Math.round((successCount / settled) * 100) : null
  const timed = deliveries.filter(d => d.response_time_ms)
  const avgResponse = timed.length
    ? Math.round(timed.reduce((sum, d) => sum + d.response_time_ms, 0) / timed.length)
    : null
  const urlParts = splitUrl(webhook.url)

  usePageBreadcrumbs(editing
    ? [{ label: webhook.name, onClick: () => setEditing(false) }, { label: 'Edit' }]
    : [{ label: webhook.name }])

  // From the health strip: show the delivery in the log, opened.
  const focusDelivery = (id) => {
    setOutcome('all')
    setExpandedDelivery(id)
    requestAnimationFrame(() => {
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      document.getElementById(`delivery-${id}`)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' })
    })
  }
  const visibleDeliveries = outcome === 'all' ? deliveries : deliveries.filter(d => d.status === outcome)
  const refetching = loadingDeliveries && hasLoadedDeliveries

  if (editing) {
    return (
      <div key="edit" className="animate-view-in">
        <BackLink onClick={() => setEditing(false)}>{webhook.name}</BackLink>
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Edit Connector</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Changes apply to the next delivery.</p>
        </div>
        <WebhookForm webhook={webhook} onSave={handleUpdate} onCancel={() => setEditing(false)} saving={saving} readOnly={readOnly} />
      </div>
    )
  }

  return (
    <div key="detail" className="space-y-6 animate-view-in">
      {/* Header */}
      <div className="animate-header-in">
        <BackLink onClick={onBack}>Connectors</BackLink>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <ConnectorMark active={webhook.is_active} size="lg" />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="truncate text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">{webhook.name}</h1>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 ring-1 ring-inset ${
                  webhook.is_active
                    ? 'bg-emerald-50 ring-emerald-600/15 dark:bg-emerald-500/10 dark:ring-emerald-400/20'
                    : 'bg-gray-50 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700'
                }`}>
                  <StatusLabel active={webhook.is_active} live />
                </span>
              </div>
              <div className="mt-1 flex min-w-0 items-center gap-1.5">
                <Globe className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                <p className="min-w-0 truncate font-mono text-[13px]" title={webhook.url}>
                  <span className="text-gray-700 dark:text-gray-200">{urlParts.host}</span>
                  <span className="text-gray-400 dark:text-gray-500">{urlParts.rest}</span>
                </p>
                <CopyButton value={webhook.url} label="Copy endpoint URL" />
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            <label className={`inline-flex h-9 items-center gap-2.5 rounded-lg border border-gray-200 px-3 text-[13px] font-medium text-gray-700 dark:border-gray-700 dark:text-gray-200 ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}>
              <span>{webhook.is_active ? 'Enabled' : 'Disabled'}</span>
              <Toggle
                checked={webhook.is_active}
                onChange={(e) => { e.stopPropagation(); if (!readOnly) onToggle(webhook.id, !webhook.is_active) }}
                disabled={readOnly}
                title={readOnly ? READ_ONLY_MESSAGE : (webhook.is_active ? 'Disable' : 'Enable')}
              />
            </label>
            <button onClick={handleTest} disabled={readOnly || testing}
              title={readOnly ? READ_ONLY_MESSAGE : 'Send a sample payload to this endpoint'}
              className={ICON_BUTTON}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Test
            </button>
            <button onClick={() => !readOnly && setEditing(true)} disabled={readOnly}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
              className={ICON_BUTTON}>
              <Edit3 className="h-4 w-4" />Edit
            </button>
            <MoreMenu
              items={[
                { label: 'Copy endpoint URL', icon: Copy, onClick: () => { navigator.clipboard?.writeText(webhook.url); toast.success('Copied', 'Endpoint URL copied to clipboard.') } },
                { label: 'Delete connector', icon: deleting ? Loader2 : Trash2, danger: true, disabled: readOnly || deleting, title: readOnly ? READ_ONLY_MESSAGE : undefined, onClick: handleDelete },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Health + configuration */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_21rem]">
        <div className="h-full animate-rise-in" style={{ '--rise-delay': '60ms' }}>
          <DeliveryHealth
            deliveries={deliveries}
            loading={!hasLoadedDeliveries}
            successCount={successCount}
            failedCount={failedCount}
            successRate={successRate}
            avgResponse={avgResponse}
            onSelect={focusDelivery}
          />
        </div>
        <div className="h-full animate-rise-in" style={{ '--rise-delay': '100ms' }}>
          <ConfigurationCard webhook={webhook} onEdit={() => setEditing(true)} readOnly={readOnly} />
        </div>
      </div>

      {/* Delivery logs */}
      <div className={`${CARD} overflow-hidden animate-rise-in`} style={{ '--rise-delay': '120ms' }}>
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-4 py-3.5 dark:border-gray-800 sm:px-5">
          <div className="mr-auto min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Delivery Logs</h2>
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 motion-safe:animate-pulse" />
              Updates every 10 seconds
            </p>
          </div>
          <Segmented
            label="Filter deliveries by outcome"
            value={outcome}
            onChange={setOutcome}
            options={[
              { value: 'all', label: 'All', count: deliveries.length },
              { value: 'success', label: 'Successful', count: successCount },
              { value: 'failed', label: 'Failed', count: failedCount },
            ]}
          />
          <button onClick={loadDeliveries}
            className={`${ICON_BUTTON} px-2.5`}
            aria-label="Refresh delivery logs"
            title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loadingDeliveries ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Refetch over existing results: a sliver of progress, and the
            log dims instead of blanking. */}
        <div className="relative h-0.5 overflow-hidden" aria-hidden="true">
          {refetching && (
            <span className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500/80 dark:bg-navy-400/80" />
          )}
        </div>

        {!hasLoadedDeliveries ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-4 px-5 py-3.5">
                <div className="skeleton h-5 w-16 rounded-full" />
                <div className="skeleton h-3.5 w-32" />
                <div className="skeleton ml-auto h-3.5 w-24" />
              </div>
            ))}
          </div>
        ) : deliveries.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center animate-panel-in">
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800">
              <Send className="h-5 w-5" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No deliveries yet</p>
            <p className="mt-1 max-w-sm text-xs text-gray-500 dark:text-gray-400">Send a test or approve a request to see delivery logs here.</p>
            <button onClick={handleTest} disabled={readOnly || testing}
              title={readOnly ? READ_ONLY_MESSAGE : undefined}
              className={`${ICON_BUTTON} mt-5`}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Send Test
            </button>
          </div>
        ) : (
          <div aria-busy={loadingDeliveries} className={`transition-opacity duration-200 ${refetching ? 'opacity-60' : 'opacity-100'}`}>
            <div className="overflow-x-auto">
              <div className="md:min-w-[49rem]">
                {/* Table header */}
                <div className={`grid ${DELIVERY_GRID} border-b border-gray-100 bg-gray-50/60 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-800 dark:bg-gray-800/30 dark:text-gray-400 sm:px-5`}>
                  <div>Status</div>
                  <div>Event</div>
                  <div className="hidden md:block">HTTP</div>
                  <div className="hidden md:block">Request</div>
                  <div className="hidden md:block">Timestamp</div>
                  <div className="hidden md:block">Duration</div>
                  <div className="hidden md:block">Download</div>
                  <div />
                </div>
                {visibleDeliveries.length === 0 ? (
                  <p className="px-5 py-10 text-center text-sm text-gray-500 animate-panel-in dark:text-gray-400">
                    No {outcome === 'success' ? 'successful' : 'failed'} deliveries in the latest {deliveries.length}.
                  </p>
                ) : (
                  <ul key={outcome} className="divide-y divide-gray-100 dark:divide-gray-800">
                    {visibleDeliveries.map((delivery, index) => (
                      <DeliveryRow
                        key={delivery.id}
                        delivery={delivery}
                        index={index}
                        open={expandedDelivery === delivery.id}
                        onToggleOpen={() => setExpandedDelivery(expandedDelivery === delivery.id ? null : delivery.id)}
                        onRetry={handleRetryDelivery}
                        retrying={retryingId === delivery.id}
                        readOnly={readOnly}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete confirmation modal - initial confirm, then linked-templates warning if backend returns 409 */}
      {deleteConfirm && (
        <DeleteDialog
          webhook={webhook}
          state={deleteConfirm}
          deleting={deleting}
          onCancel={closeDeleteDialog}
          onConfirm={performDelete}
        />
      )}
    </div>
  )
}

// ── List ─────────────────────────────────────────────────────────────────────

const LIST_GRID = 'grid-cols-[minmax(0,1fr)_auto] lg:grid-cols-[minmax(0,1fr)_minmax(0,15rem)_6.5rem_7.5rem_4.5rem]'

function WebhookRow({ webhook, index, onClick, onToggle, readOnly = false }) {
  // Holds the requested state from the click until the refetched list agrees,
  // so the switch and its label don't bounce back while the PUT is in flight.
  const [optimistic, setOptimistic] = useState(null)
  const serverActive = Boolean(webhook.is_active)
  useEffect(() => {
    if (optimistic !== null && serverActive === optimistic) setOptimistic(null)
  }, [serverActive, optimistic])
  const active = optimistic ?? serverActive

  const events = parseEvents(webhook.events)
  const { host, rest } = splitUrl(webhook.url)

  const handleToggle = async (e) => {
    e.stopPropagation()
    if (readOnly || optimistic !== null) return
    const next = !webhook.is_active
    setOptimistic(next)
    const ok = await onToggle(webhook.id, next)
    if (!ok) setOptimistic(null)
  }

  return (
    <li
      className="group relative animate-rise-in"
      style={{ '--rise-delay': `${Math.min(index, 12) * 32}ms` }}
    >
      <div className={`grid ${LIST_GRID} items-center gap-x-5 gap-y-2 px-4 py-3.5 transition-colors duration-150 group-hover:bg-gray-50/80 dark:group-hover:bg-gray-800/40 sm:px-5`}>
        <div className="flex min-w-0 items-center gap-3.5">
          <ConnectorMark active={active} />
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-2">
              {/* Stretched over the whole row; the switch sits above it. */}
              <button
                type="button"
                onClick={onClick}
                className="truncate rounded text-left text-sm font-semibold text-gray-900 transition-colors after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-navy-500/40 group-hover:text-navy-700 dark:text-gray-100 dark:group-hover:text-navy-200"
              >
                {webhook.name}
              </button>
              {webhook.secret && (
                <span title="Payloads are HMAC signed" className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span className="sr-only">Signed</span>
                </span>
              )}
            </div>
            <p className="mt-0.5 truncate font-mono text-xs" title={webhook.url}>
              <span className="text-gray-600 dark:text-gray-300">{host}</span>
              <span className="text-gray-400 dark:text-gray-500">{rest}</span>
            </p>
            {/* Below lg the columns collapse into this line. */}
            <p className="mt-1 flex items-center gap-2 text-xs text-gray-500 lg:hidden">
              <StatusLabel active={active} />
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">&middot;</span>
              <span>{events.length} event{events.length === 1 ? '' : 's'}</span>
            </p>
          </div>
        </div>

        <div className="hidden min-w-0 flex-wrap gap-1.5 lg:flex">
          {events.map(evt => <EventChip key={evt} event={evt} />)}
        </div>

        <div className="hidden lg:block">
          <StatusLabel active={active} />
        </div>

        <div className="hidden whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-gray-400 lg:block">
          {webhook.created_at ? formatDate(webhook.created_at) : '-'}
        </div>

        {/* Only the switch is raised above the row's stretched button, so a
            click anywhere else in this cell still opens the connector. */}
        <div className="pointer-events-none relative z-10 flex items-center justify-end gap-2">
          <span className="pointer-events-auto inline-flex">
            <Toggle
              checked={active}
              onChange={handleToggle}
              disabled={readOnly}
              title={readOnly ? READ_ONLY_MESSAGE : (active ? 'Disable' : 'Enable')}
            />
          </span>
          <ChevronRight
            aria-hidden="true"
            className={`h-4 w-4 text-gray-400 opacity-0 transition-all duration-300 ${EASE} motion-safe:-translate-x-1 group-hover:translate-x-0 group-hover:opacity-100 dark:text-gray-500`}
          />
        </div>
      </div>
    </li>
  )
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-gray-100 dark:divide-gray-800" aria-hidden="true">
      {[0, 1, 2, 3, 4].map(i => (
        <li key={i} className={`grid ${LIST_GRID} items-center gap-x-5 px-4 py-4 sm:px-5`}>
          <div className="flex items-center gap-3.5">
            <div className="skeleton h-9 w-9 rounded-lg" />
            <div className="space-y-2">
              <div className="skeleton h-3.5 w-32" />
              <div className="skeleton h-3 w-56" />
            </div>
          </div>
          <div className="skeleton hidden h-5 w-28 rounded-md lg:block" />
          <div className="skeleton hidden h-3.5 w-14 lg:block" />
          <div className="skeleton hidden h-3.5 w-20 lg:block" />
          <div className="skeleton ml-auto h-4 w-7 rounded-full" />
        </li>
      ))}
    </ul>
  )
}

/** Approved request → signed POST → the customer's endpoint. */
function FlowDiagram() {
  const steps = [
    { icon: CheckCircle2, label: 'Request approved' },
    { icon: Shield, label: 'Signed HTTPS POST' },
    { icon: Globe, label: 'Your endpoint' },
  ]
  return (
    <div className="flex items-start justify-center" aria-hidden="true">
      {steps.map((step, i) => {
        const Icon = step.icon
        return (
          <React.Fragment key={step.label}>
            <div className="flex w-20 flex-col items-center gap-2 animate-rise-in sm:w-28" style={{ '--rise-delay': `${120 + i * 90}ms` }}>
              <span className="flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <Icon className="h-5 w-5" />
              </span>
              <span className="text-center text-xs font-medium text-gray-600 dark:text-gray-300">{step.label}</span>
            </div>
            {i < steps.length - 1 && (
              <div className="relative mt-[21px] h-px w-6 overflow-hidden bg-gray-200 dark:bg-gray-700 sm:w-16">
                <span className="connector-flow-pulse absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-navy-500 to-transparent dark:via-navy-300" style={{ animationDelay: `${i * 0.6}s` }} />
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

function EmptyState({ onCreate, isReader }) {
  return (
    <div className="flex flex-col items-center px-6 py-14 text-center animate-panel-in">
      <FlowDiagram />
      <h3 className="mt-8 text-base font-semibold text-gray-900 dark:text-gray-100">No connectors yet</h3>
      <p className="mt-2 max-w-md text-sm leading-relaxed text-gray-500 dark:text-gray-400">
        When a request is approved, Xtract sends a signed HTTP POST to your configured URL with the full extracted data - fields, document metadata, and request info.
      </p>
      <button onClick={() => !isReader && onCreate()}
        disabled={isReader}
        title={isReader ? READ_ONLY_MESSAGE : undefined}
        className="btn btn-primary mt-6 gap-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50">
        <Plus className="h-4 w-4" />
        Add your first connector
      </button>
      <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">
        Each delivery includes <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">X-Xtract-Event</code> and <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] text-gray-600 dark:bg-gray-800 dark:text-gray-300">X-Xtract-Signature</code> headers for verification.
      </p>
    </div>
  )
}

function ListSearch({ value, onChange }) {
  const inputRef = useRef(null)
  return (
    <div role="search" className="relative min-w-0 basis-full sm:max-w-xs sm:flex-1 sm:basis-auto">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
      <input
        ref={inputRef}
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Escape') onChange('') }}
        placeholder="Search name or URL…"
        aria-label="Search connectors"
        className="h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-8 text-sm text-gray-900 transition-[border-color,box-shadow] duration-200 placeholder:text-gray-400 focus:border-navy-500 focus:outline-none focus:ring-2 focus:ring-navy-500/25 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <button
          type="button"
          onClick={() => { onChange(''); inputRef.current?.focus() }}
          aria-label="Clear search"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors animate-panel-in hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

// ===========================================================================
// WEBHOOKS PAGE
// ===========================================================================
export default function Webhooks() {
  const location = useLocation()
  const toast = useToast()
  const { user: authUser } = useAuth()
  const isReader = isReadOnlyUser(authUser)
  const [webhooks, setWebhooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [selectedWebhook, setSelectedWebhook] = useState(null)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')

  const fetchWebhooks = useCallback(async () => {
    try {
      const resp = await api.get('/webhooks')
      setWebhooks(resp.data.webhooks || [])
    } catch (err) {
      toast.error('Load Failed', 'Could not load webhooks.')
    } finally { setLoading(false) }
  }, [toast])

  useEffect(() => { fetchWebhooks() }, [fetchWebhooks])

  useEffect(() => {
    if (location.pathname !== '/webhooks') return
    if (!location.state?.fromSidebar || !location.state?.navClickAt) return

    setShowForm(false)
    setSelectedWebhook(null)
  }, [location.pathname, location.state])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchWebhooks()
    setRefreshing(false)
  }

  const handleCreate = async (data) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    setSaving(true)
    try {
      await api.post('/webhooks', data)
      toast.success('Webhook Created', 'Your webhook has been created.')
      setShowForm(false)
      fetchWebhooks()
    } catch (err) {
      toast.error('Create Failed', err?.response?.data?.error || 'Could not create connector.')
    } finally { setSaving(false) }
  }

  const handleDelete = async (id, { force = false } = {}) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    const url = force ? `/webhooks/${id}?force=true` : `/webhooks/${id}`
    try {
      await api.delete(url)
      toast.success('Webhook Deleted', 'The webhook has been removed.')
      fetchWebhooks()
    } catch (err) {
      // For 409 (linked templates) we let the caller surface a richer
      // confirmation modal, so re-throw instead of toasting.
      if (err?.response?.status === 409) {
        throw err
      }
      toast.error('Delete Failed', err?.response?.data?.error || 'Could not delete webhook.')
      throw err
    }
  }

  // Resolves true once the change is saved, so the list row can keep its
  // optimistic state until the refetch lands, or roll back on failure.
  const handleToggle = async (id, isActive) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return false
    }
    try {
      await api.put(`/webhooks/${id}`, { is_active: isActive })
      fetchWebhooks()
      if (selectedWebhook?.id === id) {
        setSelectedWebhook(prev => ({ ...prev, is_active: isActive }))
      }
      return true
    } catch (err) {
      toast.error('Toggle Failed', 'Could not update webhook status.')
      return false
    }
  }

  const handleTest = async (id) => {
    if (isReader) {
      toast.error('Read-only access', READ_ONLY_MESSAGE)
      return
    }
    try {
      await api.post(`/webhooks/${id}/test`)
      toast.success('Test Sent', 'A test payload has been sent. Check delivery logs.')
    } catch (err) {
      toast.error('Test Failed', err?.response?.data?.error || 'Could not send test.')
    }
  }

  usePageBreadcrumbs(showForm && !selectedWebhook ? [{ label: 'New connector' }] : null)

  const activeCount = useMemo(() => webhooks.filter(w => w.is_active).length, [webhooks])
  const visibleWebhooks = useMemo(() => {
    const q = query.trim().toLowerCase()
    return webhooks.filter(w => {
      if (statusFilter === 'active' && !w.is_active) return false
      if (statusFilter === 'disabled' && w.is_active) return false
      if (!q) return true
      return [w.name, w.url, w.events].some(v => (v || '').toLowerCase().includes(q))
    })
  }, [webhooks, query, statusFilter])

  // ─── Detail view ───
  if (selectedWebhook) {
    return (
      <div className="w-full">
        <WebhookDetailPanel
          key={selectedWebhook.id}
          webhook={selectedWebhook}
          onBack={() => { setSelectedWebhook(null); fetchWebhooks() }}
          onDelete={handleDelete}
          onToggle={handleToggle}
          onTest={handleTest}
          toast={toast}
          readOnly={isReader}
        />
      </div>
    )
  }

  // ─── Create form view ───
  if (showForm) {
    return (
      <div key="create" className="w-full animate-view-in">
        <BackLink onClick={() => setShowForm(false)}>Connectors</BackLink>
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Create Connector</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Deliver approved extraction data to an HTTPS endpoint you control.
          </p>
        </div>
        <WebhookForm onSave={handleCreate} onCancel={() => setShowForm(false)} saving={saving} readOnly={isReader} />
      </div>
    )
  }

  const filtered = query.trim() !== '' || statusFilter !== 'all'

  // ─── List view ───
  return (
    <div key="list" className="w-full space-y-6 animate-view-in">
      {/* Header */}
      <div className="flex flex-col gap-4 animate-header-in sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3.5">
          <div className="hidden h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10 sm:flex">
            <Webhook className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Connectors</h1>
              {!loading && webhooks.length > 0 && (
                <span key={webhooks.length} className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold tabular-nums text-navy-700 ring-1 ring-inset ring-navy-200 animate-panel-in dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                  {webhooks.length}
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Configure outbound connectors to send extracted data to your APIs, Azure Functions, or any HTTPS endpoint.
            </p>
          </div>
        </div>
        {/* Only once a connector exists: until then the empty state's own
            button is the single call to action. Hidden while loading too, so
            it doesn't flash in and out for an org with no connectors. */}
        {!loading && webhooks.length > 0 && (
          <button onClick={() => !isReader && setShowForm(true)}
            disabled={isReader}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            className="btn btn-primary flex-shrink-0 gap-1.5 self-start text-sm animate-panel-in disabled:cursor-not-allowed disabled:opacity-50 sm:self-auto">
            <Plus className="h-4 w-4" />
            Add Connector
          </button>
        )}
      </div>

      <div className={`${CARD} overflow-hidden animate-rise-in`} style={{ '--rise-delay': '60ms' }}>
        {loading ? (
          <ListSkeleton />
        ) : webhooks.length === 0 ? (
          <EmptyState onCreate={() => setShowForm(true)} isReader={isReader} />
        ) : (
          <>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-3 dark:border-gray-800 sm:px-5">
              <ListSearch value={query} onChange={setQuery} />
              <Segmented
                label="Filter connectors by status"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All', count: webhooks.length },
                  { value: 'active', label: 'Active', count: activeCount },
                  { value: 'disabled', label: 'Disabled', count: webhooks.length - activeCount },
                ]}
              />
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing}
                aria-label="Refresh connectors"
                title="Refresh"
                className={`${ICON_BUTTON} ml-auto px-2.5`}
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {/* Column headings */}
            <div className={`hidden ${LIST_GRID} gap-x-5 border-b border-gray-100 bg-gray-50/60 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:border-gray-800 dark:bg-gray-800/30 dark:text-gray-400 lg:grid`}>
              <div>Connector</div>
              <div>Events</div>
              <div>Status</div>
              <div>Created</div>
              <div className="text-right"><span className="sr-only">Enabled</span></div>
            </div>

            {visibleWebhooks.length === 0 ? (
              <div className="flex flex-col items-center px-6 py-14 text-center animate-panel-in">
                <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-gray-800">
                  <SearchX className="h-5 w-5" />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">No matching connectors</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Try a different search or status.</p>
                {filtered && (
                  <button
                    type="button"
                    onClick={() => { setQuery(''); setStatusFilter('all') }}
                    className="mt-4 text-xs font-medium text-navy-700 underline-offset-2 hover:underline dark:text-navy-300"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <ul key={statusFilter} className="divide-y divide-gray-100 dark:divide-gray-800">
                {visibleWebhooks.map((wh, index) => (
                  <WebhookRow key={wh.id} webhook={wh} index={index}
                    onClick={() => setSelectedWebhook(wh)}
                    onToggle={handleToggle}
                    readOnly={isReader}
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}
